/**
 * Grid overlay component
 * Displays grid preview on the map
 * Supports both rectangle and polygon selection display
 */

import { useMemo } from 'react';
import { Rectangle, Polygon, Tooltip, Pane } from 'react-leaflet';
import type { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import type { Bounds, GridPreview, GridCell, GridCellStatus, PolygonCoordinates } from '@/types/grid';
import { CELL_STATUS_COLORS } from '@/utils/constants';

interface GridOverlayProps {
  bounds: Bounds;
  gridPreview?: GridPreview;
  cells?: GridCell[];
  /** Optional polygon coordinates for freehand selection display */
  polygon?: PolygonCoordinates;
  showGrid?: boolean;
  opacity?: number;
  onCellClick?: (cell: GridCell) => void;
  selectedCellId?: string;
}

export function GridOverlay({
  bounds,
  gridPreview,
  cells,
  polygon,
  showGrid = true,
  opacity = 0.2,
  onCellClick,
  selectedCellId,
}: GridOverlayProps) {
  // Convert polygon to Leaflet format
  const polygonPositions: LatLngExpression[] | null = useMemo(() => {
    if (!polygon || polygon.length < 3) return null;
    return polygon.map(p => [p.lat, p.lng] as LatLngExpression);
  }, [polygon]);
  // If we have actual cells data, use that
  if (cells && cells.length > 0) {
    return (
      <Pane name="grid-pane" style={{ zIndex: 400 }}>
        {cells.map((cell) => {
          const cellBounds: LatLngBoundsExpression = [
            [cell.bounds.south, cell.bounds.west],
            [cell.bounds.north, cell.bounds.east],
          ];

          const isSelected = cell.id === selectedCellId;
          const color = CELL_STATUS_COLORS[cell.status];

          return (
            <Rectangle
              key={cell.id}
              bounds={cellBounds}
              pathOptions={{
                color: isSelected ? '#1677ff' : color,
                weight: isSelected ? 2 : 1,
                fillColor: color,
                fillOpacity: cell.status === 'pending' ? 0.25 : 0.4,
              }}
              eventHandlers={{
                click: () => onCellClick?.(cell),
              }}
            >
              <Tooltip sticky>
                <div>
                  <strong>Cell {cell.index !== undefined ? cell.index + 1 : cell.id}</strong>
                  <br />
                  Status: {cell.status}
                  {cell.placesFound !== undefined && (
                    <>
                      <br />
                      Places: {cell.placesFound}
                    </>
                  )}
                </div>
              </Tooltip>
            </Rectangle>
          );
        })}
      </Pane>
    );
  }

  // Generate preview grid from bounds
  if (!showGrid || !gridPreview || !bounds) {
    return null;
  }

  // Use pre-filtered cells from gridPreview (already filtered by polygon if applicable)
  const previewCells = gridPreview.cells;

  // Determine colors based on selection type
  const isPolygonSelection = !!polygonPositions;
  const primaryColor = isPolygonSelection ? '#722ed1' : '#1677ff';

  return (
    <Pane name="grid-preview-pane" style={{ zIndex: 400 }}>
      {/* Show polygon outline if provided, otherwise show rectangle bounds */}
      {polygonPositions ? (
        <Polygon
          positions={polygonPositions}
          pathOptions={{
            color: primaryColor,
            weight: 2,
            fill: false,
          }}
        />
      ) : (
        <Rectangle
          bounds={[
            [bounds.south, bounds.west],
            [bounds.north, bounds.east],
          ]}
          pathOptions={{
            color: primaryColor,
            weight: 2,
            fill: false,
          }}
        />
      )}

      {/* Grid cells - using pre-filtered cells from gridPreview */}
      {previewCells.map((cell, index) => (
        <Rectangle
          key={cell.id}
          bounds={[
            [cell.south, cell.west],
            [cell.north, cell.east],
          ]}
          pathOptions={{
            color: primaryColor,
            weight: 0.5,
            fillColor: primaryColor,
            fillOpacity: opacity,
          }}
        >
          <Tooltip>
            Cell {index + 1}
          </Tooltip>
        </Rectangle>
      ))}
    </Pane>
  );
}

/**
 * Simple bounds rectangle
 */
export function BoundsOverlay({
  bounds,
  color = '#1677ff',
  fillOpacity = 0.1,
  label,
}: {
  bounds: Bounds;
  color?: string;
  fillOpacity?: number;
  label?: string;
}) {
  if (!bounds) return null;

  const rectBounds: LatLngBoundsExpression = [
    [bounds.south, bounds.west],
    [bounds.north, bounds.east],
  ];

  return (
    <Rectangle
      bounds={rectBounds}
      pathOptions={{
        color,
        weight: 2,
        fillColor: color,
        fillOpacity,
      }}
    >
      {label && <Tooltip permanent>{label}</Tooltip>}
    </Rectangle>
  );
}

export default GridOverlay;
